<template>
  <PageLoader :loading="isLoading">
    <FormWizardLayout
      v-if="!isLoading"
      :is-footer-visible="isFooterVisible"
      :is-side-bar-visible="isSideBarVisible"
      :is-side-bar-mobile-menu-visible="isSideBarMobileMenuVisible"
    >
      <template slot="header">
        <FormWizardHeaderBar
          :title="headerTitleText"
          :logo-link="goBackLink"
          :last-updated-at="lastUpdatedAt"
          :on-save-and-exit="onSaveAndExit"
          :on-toggle-mobile-menu="onToggleMobileMenu"
          :hide-actions="!isSideBarVisible"
          :is-requesting="isRequesting"
        />
      </template>

      <template slot="sidebar">
        <FormWizardSideBarMenu :items="sideBarItems" :on-item-click="onSideBarItemClick" />
      </template>

      <template slot="main">
        <transition :name="stepAnimationName" mode="out-in">
          <component
            :is="currentStep.component"
            v-if="!isLoading"
            :key="currentStepKey"
            v-model="forms[currentStepKey]"
            :forms="forms"
            :$v="$v.forms[currentStepKey]"
            :investor="investor"
          />
        </transition>
      </template>

      <template slot="preview">
        <FormWizardPreviewFrame>
          <InvestorFormPreview :investor="previewInvestor" />
        </FormWizardPreviewFrame>
      </template>

      <template slot="footer">
        <FormWizardNavigationBar
          :on-next="onNext"
          :button-next-text="buttonNextText"
          :button-next-disabled="!canGoNext"
          :button-next-loading="isRequesting"
          :on-previous="currentStepIndex > 1 ? onPrevious : undefined"
          :button-previous-disabled="!canGoBack"
          :hide-text="currentStepIndex === -1"
          :current-step="currentStepIndex + 1"
          :total-steps="stepCount"
        />
      </template>
    </FormWizardLayout>
  </PageLoader>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { required, requiredIf, email, maxLength, minLength } from 'vuelidate/lib/validators';

import take from 'lodash/take';
import every from 'lodash/every';
import get from 'lodash/get';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import omitBy from 'lodash/omitBy';
import map from 'lodash/map';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';

import {
  steps,
  STEP_ELIGIBILITY,
  STEP_USER_SOURCE,
  STEP_SETTING_THE_SCENE,
  STEP_BASICS,
  STEP_STAGE,
  STEP_ABOUT,
  STEP_INDUSTRY,
  STEP_MARKET,
  STEP_COUNTRY,
  STEP_KEY_PEOPLE,
  STEP_INVESTMENTS,
  STEP_IMPACT,
  STEP_CONTACT,
  STEP_REVIEW,
  STEP_MANAGERS,
  STEP_CONFIRMATION,
  MODE_UPDATE,
  MODE_CREATE
} from './steps';
import InvestorFormPreview from './InvestorFormPreview';
import checkedValidator from '@/validators/checkedValidator';
import { urlValidator, linkedinUrlValidator } from '@/validators/urlValidator';
import { isEmptyDeep } from '@/helpers/helpers';

import FormWizardLayout from '@/components/FormWizardLayout';
import FormWizardSideBarMenu from '@/components/FormWizardSideBarMenu';
import FormWizardHeaderBar from '@/components/FormWizardHeaderBar';
import FormWizardNavigationBar from '@/components/FormWizardNavigationBar';
import FormWizardPreviewFrame from '@/components/FormWizardPreviewFrame';
import Investor from '@/models/Investor';
import PageLoader from '@/components/PageLoader';

import { investorRequireInvestments } from '@/constants';

export default {
  name: 'InvestorForm',
  components: {
    PageLoader,
    FormWizardPreviewFrame,
    FormWizardLayout,
    FormWizardSideBarMenu,
    FormWizardHeaderBar,
    FormWizardNavigationBar,
    InvestorFormPreview
  },
  mixins: [validationMixin],
  layout: 'plain',
  props: {
    investorKey: {
      type: String
    }
  },
  data() {
    return {
      mode: MODE_CREATE,
      isLoading: true,
      isSideBarMobileMenuVisible: false,
      currentStepKey: STEP_SETTING_THE_SCENE,
      stepAnimationName: 'fade-slide-left',
      lastUpdatedAt: null,
      lastSavedForms: null,
      isRequesting: false,
      investor: null, // draft || existing company.
      goBackPath: undefined,
      forms: {
        [STEP_ELIGIBILITY]: {
          criterias: false
        },
        [STEP_USER_SOURCE]: {
          source: undefined,
          sourceText: undefined
        },
        [STEP_SETTING_THE_SCENE]: {
          logoImage: undefined,
          name: undefined,
          intro: undefined
        },
        [STEP_IMPACT]: {
          sdgs: [],
          isImpact: undefined
        },
        [STEP_BASICS]: {
          category: null,
          website: undefined,
          investmentRanges: undefined
        },
        [STEP_STAGE]: {
          stages: undefined
        },
        [STEP_ABOUT]: {
          about: undefined
        },
        [STEP_INDUSTRY]: {
          industries: []
        },
        [STEP_MARKET]: {
          markets: undefined
        },
        [STEP_COUNTRY]: {
          countryCodes: undefined,
          regions: undefined
        },
        [STEP_KEY_PEOPLE]: {
          keyPeople: []
        },
        [STEP_INVESTMENTS]: {
          investments: []
        },
        [STEP_MANAGERS]: {
          managers: []
        },
        [STEP_CONTACT]: {
          contactPreferences: undefined,
          contactPhone: undefined,
          contactEmail: undefined,
          contactWebsite: undefined,
          contactText: undefined
        }
      }
    };
  },
  computed: {
    isBusinessAngel() {
      return this.forms[STEP_BASICS].category === 'ANGEL';
    },
    headerTitleText() {
      return this.mode === MODE_CREATE ? 'Create investor profile' : 'Update investor profile';
    },
    goBackLink() {
      const fallback =
        this.mode === MODE_CREATE
          ? '/investors/join'
          : `/client/investors/${this.investorKey}/profile`;

      return this.goBackPath || fallback;
    },
    userIsLoggedIn() {
      return this.$store.getters['user/isLoggedIn'];
    },
    allSteps() {
      return steps;
    },
    steps() {
      return steps.filter((step) => {
        if (this.forms[STEP_BASICS].category === 'ANGEL' && step.key === STEP_KEY_PEOPLE) {
          return false;
        }

        if (this.mode === MODE_CREATE) {
          return true;
        }

        return ![STEP_ELIGIBILITY, STEP_USER_SOURCE, STEP_REVIEW, STEP_CONFIRMATION].includes(
          step.key
        );
      });
    },
    formData() {
      const data = this.steps
        .filter((step) => ![STEP_ELIGIBILITY, STEP_REVIEW, STEP_CONFIRMATION].includes(step.key))
        .map((step) => cloneDeep(this.forms[step.key]))
        .reduce(
          (acc, item) => ({
            ...acc,
            ...item
          }),
          {}
        );

      return omitBy(data, isEmptyDeep);
    },
    previewInvestor() {
      return new Investor(this.formData);
    },
    currentStep() {
      return find(this.steps, {
        key: this.currentStepKey
      });
    },
    sideBarItems() {
      const steps = this.steps
        .filter((item) => !item.hidden)
        // enrich with 'focused' | 'completed' | 'error' states
        .map((item) => ({
          ...item,
          completed: item.hidden || (this.$v.forms[item.key] && !this.$v.forms[item.key].$invalid),
          focused: item.key === this.currentStepKey,
          error:
            this.$v.forms[item.key] &&
            (this.$v.forms[item.key].$anyError ||
              (this.mode === MODE_UPDATE && this.$v.forms[item.key].$invalid))
        }));

      return (
        steps
          // determine if disabled (if all previus items is 'completed')
          .map((item, index) => ({
            ...item,
            optional: this.getIsOptional(item),
            disabled:
              this.mode === MODE_CREATE &&
              !every(take(steps, index).filter((step) => !step.optional), {
                completed: true
              })
          }))
      );
    },
    isSideBarVisible() {
      const stepsWithout = [STEP_ELIGIBILITY, STEP_USER_SOURCE, STEP_CONFIRMATION];
      return !stepsWithout.includes(this.currentStepKey);
    },
    isFooterVisible() {
      const stepsWithout = [STEP_CONFIRMATION];
      return this.mode === MODE_CREATE && !stepsWithout.includes(this.currentStepKey);
    },
    buttonNextText() {
      return this.currentStepKey === STEP_REVIEW ? 'Submit' : 'Next';
    },
    currentStepIndex() {
      return this.steps.indexOf(this.currentStep);
    },
    stepCount() {
      return this.steps.length;
    },
    canGoBack() {
      return this.currentStepIndex > 1;
    },
    canGoNext() {
      return this.canGoToIndex(this.currentStepIndex + 1);
    },
    hasInvestor() {
      return !!this.investor;
    },
    isDraft() {
      return this.hasInvestor && this.investor.status === 'DRAFT';
    },
    isUnassigned() {
      return this.$route.query.unassigned;
    }
  },
  watch: {
    [`forms.${STEP_BASICS}.category`](value) {
      if (value === 'ANGEL') {
        this.forms[STEP_KEY_PEOPLE].keyPeople = [];
      }
    },
    currentStepIndex(newStepIndex, prevStepIndex) {
      const nextStep = this.steps[newStepIndex];
      const prevStep = this.steps[prevStepIndex];

      // when switching between sidebar visibility do a fade animation instead.
      if (prevStep?.hidden || nextStep?.hidden) {
        // not found in `steps` its either "intro" our "outro"
        this.stepAnimationName = 'fade--page';
      } else {
        this.stepAnimationName =
          newStepIndex > prevStepIndex ? 'fade-slide-left' : 'fade-slide-right';
      }

      this.$router.push({
        query: {
          ...this.$route.query,
          step: !nextStep?.hidden ? nextStep?.key : undefined
        }
      });
    },

    /**
     * integrate query-string routing (steps)
     */
    $route: {
      immediate: true,
      handler(newRoute) {
        const step = get(newRoute, 'query.step');
        if (step !== this.currentStepKey) {
          const index = findIndex(this.steps, { key: step });

          if (index !== -1 && this.canGoToIndex(index)) {
            this.currentStepKey = this.steps[index].key;
          }
        }

        // hide mobile-menu when navigating.
        if (this.isSideBarMobileMenuVisible) {
          this.isSideBarMobileMenuVisible = false;
        }
      }
    }
  },
  async mounted() {
    if (this.userIsLoggedIn) {
      await this.onAppInitialized();
    } else if (this.mode === MODE_CREATE) {
      this.currentStepKey = STEP_ELIGIBILITY;
    }

    this.isLoading = false;
  },
  methods: {
    getIsOptional(step) {
      if (step.key === STEP_INVESTMENTS) {
        return investorRequireInvestments.indexOf(this.forms[STEP_BASICS].category) === -1;
      }
      return step.optional;
    },
    buildRequestBody() {
      const data = cloneDeep(this.formData);

      data.logoImage = get(data, 'logoImage.id');

      data.keyPeople = map(data.keyPeople, (item) => ({
        ...item,
        image: get(item, 'image.id')
      }));

      data.investments = map(data.investments, (item) => ({
        name: item.name,
        category: item.category,
        website: item.website,
        company: item.company ? item.company.id : undefined
      }));

      data.countryCodes = data.countryCodes.filter((country) => country);

      // console.log(data);

      return data;
    },

    beforeRouteEnter(to, from) {
      if (from.name && !from.path.includes('/client/startups')) {
        // register where user came from so we can return to same page
        this.goBackPath = from.path;
      }
    },

    /**
     * https://stackoverflow.com/questions/42045433/vue-router-beforerouteleave-doesnt-stop-subcomponents
     */
    async beforeRouteLeave(to, from, next) {
      const current = JSON.parse(JSON.stringify(this.formData));
      const isDirty = this.lastSavedForms && !isEqual(this.lastSavedForms, current);

      if (this.userIsLoggedIn && isDirty) {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          'Do you really want to leave? You have unsaved changes!',
          {
            title: 'Unsaved changes',
            modalClass: 'modal-bb-squared',
            okTitle: 'Continue',
            cancelTitle: 'Cancel',
            cancelVariant: 'outline',
            footerClass: 'd-flex'
          }
        );

        if (confirmed) {
          next();
        }
      } else {
        next();
      }
    },

    async onAppInitialized() {
      await this.loadAndPopulateInvestorData();

      this.mode = this.hasInvestor && !this.isDraft ? MODE_UPDATE : MODE_CREATE;
      this.currentStepKey = this.hasInvestor ? STEP_SETTING_THE_SCENE : STEP_USER_SOURCE;

      // @workaround whenever a draft or company was loaded, no need to accept criterias / fill in user-source.
      this.forms[STEP_ELIGIBILITY].criterias = true;

      if (this.hasInvestor) {
        this.forms[STEP_USER_SOURCE].source = 'xx';
      }

      this.lastSavedForms = JSON.parse(JSON.stringify(this.formData));

      /**
       * if deep-linking to a step, set that as `current`.
       */
      const step = get(this.$route, 'query.step');
      if (step !== this.currentStepKey) {
        const index = findIndex(this.steps, { key: step });

        if (index !== -1 && this.canGoToIndex(index)) {
          this.currentStepKey = this.steps[index].key;
        }
      }
    },

    async loadAndPopulateInvestorData() {
      const investorKey = this.$route.params.investor;

      // load company-data (draft | existing) for population.
      if (investorKey) {
        const { data } = await this.$axios.get('/investors/' + investorKey);

        this.investor = new Investor(data);

        // if we loaded form-data (draft | company-data) then populate the form.
        this.allSteps.forEach((step) => {
          if (this.forms[step.key]) {
            Object.keys(this.forms[step.key]).forEach((prop) => {
              this.forms[step.key][prop] = data[prop];
            });
          }
        });
      }
    },
    onToggleMobileMenu() {
      this.isSideBarMobileMenuVisible = !this.isSideBarMobileMenuVisible;
    },
    async onSave() {
      if (this.isRequesting) {
        return;
      }

      if (this.mode === MODE_UPDATE) {
        // can only save if form is valid. when updating company.
        this.$v.forms.$touch();
      } else {
        this.$v.forms[STEP_SETTING_THE_SCENE].name.$touch(); // in order to save draft, there MUST be a title.
      }

      if (this.$v.forms.$anyError) {
        await this.$bvModal.msgBoxOk(
          'Please fill out missing steps marked with X and submit again',
          {
            title: 'Missing fields',
            modalClass: 'modal-bb-squared'
          }
        );

        return Promise.reject(new Error('Missing fields'));
      }

      try {
        this.isRequesting = true;

        await (this.mode === MODE_CREATE
          ? this.requestCreateOrUpdateDraft()
          : this.requestUpdate());

        this.lastUpdatedAt = new Date().toISOString();
        this.lastSavedForms = JSON.parse(JSON.stringify(this.formData));

        // reload user data
        await this.$store.dispatch('user/load');

        this.$v.$reset();
      } catch (err) {
        alert(err.message);
        console.log(err);
      }

      setTimeout(() => {
        this.isRequesting = false;
      }, 250);
    },
    async onSaveAndExit() {
      try {
        await this.onSave();
        this.onGoBack();
      } catch (err) {
        console.log(err);
      }
    },

    onGoBack() {
      this.$router.push({
        path: this.goBackLink
      });
    },

    onSideBarItemClick(key) {
      this.currentStepKey = key;
    },
    canGoToIndex(stepIndex) {
      const mustBeValid = take(this.steps, stepIndex)
        .map((step) => this.$v.forms[step.key])
        .filter((step) => !!step);

      return every(mustBeValid, { $invalid: false });
    },

    async onNext() {
      const currentForm = this.$v.forms[this.currentStepKey];
      if (currentForm) {
        currentForm.$touch(); // will trigger errors to appear.

        if (currentForm.$error) {
          return;
        }
      }

      switch (true) {
        case this.currentStepKey === STEP_ELIGIBILITY && !this.userIsLoggedIn:
          this.$store.dispatch('modal/auth', {
            onCancel: this.onAuthCancel,
            onAuthSuccess: this.onAuthSuccess
          });
          break;

        case this.currentStepKey === STEP_USER_SOURCE:
          this.isRequesting = true;

          try {
            await this.$axios.put('/users/me', {
              ...this.forms[STEP_USER_SOURCE]
            });
          } catch (err) {
            console.log(err);
          }

          this.isRequesting = false;

          this.currentStepKey = STEP_SETTING_THE_SCENE;
          break;

        // create new investor.
        case this.currentStepKey === STEP_REVIEW:
          // Trigger errors
          this.$v.forms.$touch();

          if (this.$v.forms.$anyError) {
            await this.$bvModal.msgBoxOk(
              'Please fill out missing steps marked with X and submit again',
              {
                title: 'Missing fields',
                modalClass: 'modal-bb-squared'
              }
            );
          } else {
            await this.requestCreate();
          }
          break;

        // goto next step.
        default:
          this.currentStepKey = this.steps[this.currentStepIndex + 1].key;
          break;
      }
    },

    onAuthCancel() {
      this.hideAuthModal();
    },
    onAuthSuccess() {
      this.hideAuthModal();
      this.onAppInitialized();
    },
    hideAuthModal() {
      this.$store.dispatch('modal/cancel');
    },

    onPrevious() {
      if (this.currentStepIndex > 0) {
        this.currentStepKey = this.steps[this.currentStepIndex - 1].key;
      }
    },
    async sendManagerInvites(investorKey, managers) {
      for (const manager of managers) {
        await this.$axios
          .post('/investors/' + investorKey + '/managers', manager)
          .then(function(response) {
            // console.log('axios response:', response);
          })
          .catch(function(error) {
            console.log(error);
          });
      }
    },
    async requestCreate() {
      if (this.isRequesting) {
        return;
      }

      this.isRequesting = true;

      try {
        const params = {};

        if (this.isUnassigned) {
          params.unassigned = 1;
        }

        const uri = this.hasInvestor
          ? '/investors/' + this.investor.key + '/submit'
          : '/investors/';
        const response = await this.$axios.post(uri, this.buildRequestBody(), {
          params
        });

        // Create InvestorManagers
        if (this.formData.managers && this.formData.managers.length > 0) {
          await this.sendManagerInvites(response.data.investor.key, this.formData.managers);
        }

        // refresh user data (because now it includes the created company)
        this.$store.dispatch('user/load');

        this.lastSavedForms = JSON.parse(JSON.stringify(this.formData));
        this.investor = new Investor(response.data.investor);
        this.currentStepKey = STEP_CONFIRMATION;

        this.$gtm.push({ event: 'fb', fbEvent: 'InvestorCreated' });
        this.$gtm.push({
          event: 'ga.event',
          category: 'client',
          action: 'InvestorCreated',
          label: response.data.investor.key
        });

        this.$engagement({
          type: 'investors.create'
        });

        // console.log('created', response);
      } catch (err) {
        console.log(err);
        alert(err.message);
      }

      setTimeout(() => {
        this.isRequesting = false;
      }, 250);
    },

    async requestUpdate() {
      const uri = '/investors/' + this.investor.key;
      const response = await this.$axios.put(uri, this.buildRequestBody());

      // console.log('updated', response);

      this.$engagement({
        type: 'investors.update'
      });

      return response;
    },

    async requestCreateOrUpdateDraft() {
      const params = {};

      if (this.hasInvestor) {
        params.draft = this.investor.key;
      }

      if (this.isUnassigned) {
        params.unassigned = 1;
      }

      const response = await this.$axios.post('/investors/draft', this.buildRequestBody(), {
        params
      });

      // console.log('updated-draft', response)

      this.$engagement({
        type: 'investors.update'
      });

      return response.data;
    }
  },
  validations() {
    let validations = {
      [STEP_SETTING_THE_SCENE]: {
        logoImage: {
          required
        },
        name: {
          required,
          maxLength: maxLength(100)
        },
        intro: {
          required,
          minLength: minLength(100),
          maxLength: maxLength(2000)
        }
      },
      [STEP_BASICS]: {
        category: {
          required
        },
        website: {
          required,
          urlValidator
        },
        investmentRanges: {
          required: requiredIf(function() {
            return this.forms[STEP_BASICS].category !== 'ACCELERATORS';
          })
        }
      },
      [STEP_STAGE]: {
        stages: {
          required
        }
      },
      [STEP_IMPACT]: {
        isImpact: { required },
        sdgs: {
          required: requiredIf(function() {
            return this.forms[STEP_IMPACT].isImpact;
          })
        }
      },
      [STEP_ABOUT]: {
        about: {
          maxLength: maxLength(11000)
        }
      },
      [STEP_INDUSTRY]: {
        industries: {
          required
        }
      },
      [STEP_MARKET]: {
        markets: {
          required
        }
      },
      [STEP_COUNTRY]: {
        countryCodes: {
          required
        },
        regions: {}
      },
      [STEP_KEY_PEOPLE]: {
        keyPeople: {
          $each: {
            name: {
              required
            },
            title: {
              required,
              maxLength: maxLength(100)
            },
            linkedinUrl: {
              linkedinUrlValidator
            }
          }
        }
      },
      [STEP_INVESTMENTS]: {
        investments: {
          required: requiredIf(function() {
            return investorRequireInvestments.indexOf(this.forms[STEP_BASICS].category) > -1;
          })
        }
      },
      [STEP_MANAGERS]: {
        managers: {
          $each: {
            name: {
              first: {
                required,
                maxLength: maxLength(50)
              },
              last: {
                required,
                maxLength: maxLength(50)
              }
            },
            email: {
              required,
              email
            }
          }
        }
      },
      [STEP_CONTACT]: {
        contactPreferences: {},
        contactEmail: {
          required: requiredIf(function() {
            return (
              (this.forms[STEP_CONTACT].contactPreferences || []).indexOf('email') > -1 ||
              (this.forms[STEP_CONTACT].contactPreferences || []).indexOf('signup') > -1
            );
          }),
          email
        },
        contactText: {
          maxLength: maxLength(500)
        },
        contactPhone: {
          required: requiredIf(function() {
            return (this.forms[STEP_CONTACT].contactPreferences || []).indexOf('phone') > -1;
          })
        },
        contactWebsite: {
          required: requiredIf(function() {
            return (this.forms[STEP_CONTACT].contactPreferences || []).indexOf('website') > -1;
          }),
          urlValidator
        }
      }
    };

    /**
     * on create mode we add eligibility validations.
     */
    if (this.mode === MODE_CREATE) {
      validations = {
        ...validations,
        [STEP_ELIGIBILITY]: {
          criterias: {
            required,
            checkedValidator
          }
        },
        [STEP_USER_SOURCE]: {
          source: {
            required
          },
          sourceText: {
            required: requiredIf(function() {
              return this.forms[STEP_USER_SOURCE].source === 'other';
            })
          }
        }
      };
    }

    return {
      forms: validations
    };
  }
};
</script>
